import type { LayerDef, Legend, LegendContext } from "../types";

/**
 * Climate, at the two resolutions the data actually has.
 *
 * **The projection grid** is ALADIN-Climat's native ~8 km mesh — 335 cells over
 * the whole region. It is drawn as those squares, undisguised: a smooth
 * interpolation would look better and would be a claim about parcel-level
 * precision that the model cannot support. Every scenario and horizon rides on
 * each cell as its own attribute, so changing what is shown is a paint change,
 * never a refetch.
 *
 * **The heat island** is the Institut Paris Region's classification of 237,000
 * morphological blocks — three orders of magnitude finer, and the layer that
 * answers whether a specific flat is bearable in August. Day and night are
 * separate measures because they have separate causes: daytime heat is surfaces
 * and shade, night-time heat is the mass that spent all day storing it.
 */

/** Attribute key for a scenario and horizon, matching what the ETL writes. */
export function climateKey(scenario: string, horizon: string): string {
  return horizon === "REF"
    ? "ref"
    : `${scenario.replace("RCP", "").replace(".", "")}_${horizon.toLowerCase()}`;
}

export type ClimateMetricId =
  | "mean_temp"
  | "tropical_nights"
  | "heatwave_days"
  | "summer_days"
  | "cooling_dd"
  | "dry_spell_days"
  | "heavy_rain_days";

interface ClimateMetric {
  label: string;
  unit: string;
  /** Tile-attribute prefix, matching `TILE_ATTRS` in `etl/mete_etl/climate.py`. */
  prefix: string;
  stops: { value: number; color: string }[];
  format: (v: number) => string;
  note: string;
}

/**
 * The heat indicators run cool-to-hot rather than pale-yellow-to-red. The price
 * choropleth below them is already a yellow-to-red ramp, and two warm scales
 * stacked read as one — the blue end is what makes this layer separable.
 */
export const CLIMATE_METRICS: Record<ClimateMetricId, ClimateMetric> = {
  mean_temp: {
    label: "Mean temperature",
    unit: "°C",
    prefix: "mean",
    stops: [
      { value: 9, color: "#c6dbef" },
      { value: 11, color: "#ffffbf" },
      { value: 13, color: "#fdae61" },
      { value: 15, color: "#e34a33" },
      { value: 17, color: "#7f0025" },
    ],
    format: (v) => `${v.toFixed(1)}°`,
    note: "Annual mean over the whole period, not a single year.",
  },
  tropical_nights: {
    label: "Tropical nights",
    unit: "nights a year above 20 °C",
    prefix: "trop",
    stops: [
      { value: 0, color: "#c6dbef" },
      { value: 5, color: "#ffffbf" },
      { value: 15, color: "#feb24c" },
      { value: 30, color: "#f03b20" },
      { value: 50, color: "#5c0018" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "The measure that decides whether a top-floor flat is liveable in August.",
  },
  heatwave_days: {
    label: "Heatwave days",
    unit: "days a year",
    prefix: "heat",
    stops: [
      { value: 0, color: "#c6dbef" },
      { value: 15, color: "#ffffbf" },
      { value: 40, color: "#feb24c" },
      { value: 80, color: "#f03b20" },
      { value: 130, color: "#5c0018" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "Days inside a spell the model counts as a heatwave.",
  },
  summer_days: {
    label: "Summer days",
    unit: "days a year above 25 °C",
    prefix: "summ",
    stops: [
      { value: 20, color: "#c6dbef" },
      { value: 40, color: "#ffffbf" },
      { value: 70, color: "#feb24c" },
      { value: 100, color: "#f03b20" },
      { value: 140, color: "#5c0018" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "",
  },
  cooling_dd: {
    label: "Cooling demand",
    unit: "degree-days a year",
    prefix: "cool",
    stops: [
      { value: 0, color: "#f7fbff" },
      { value: 50, color: "#c6dbef" },
      { value: 150, color: "#6baed6" },
      { value: 350, color: "#2171b5" },
      { value: 700, color: "#08306b" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "The direct link to a running cost: degree-days are what air conditioning is billed by.",
  },
  dry_spell_days: {
    label: "Longest dry spell",
    unit: "consecutive days",
    prefix: "dry",
    stops: [
      { value: 10, color: "#f7f4e8" },
      { value: 18, color: "#e6d8a8" },
      { value: 26, color: "#c9a227" },
      { value: 34, color: "#96702a" },
      { value: 45, color: "#5c421a" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "Long dry spells are what shrink clay soils, so this and the clay layer compound.",
  },
  heavy_rain_days: {
    label: "Heavy rain days",
    unit: "days a year above 20 mm",
    prefix: "heavy",
    stops: [
      { value: 0, color: "#f7fcf0" },
      { value: 2, color: "#ccebc5" },
      { value: 4, color: "#7bccc4" },
      { value: 7, color: "#2b8cbe" },
      { value: 11, color: "#084081" },
    ],
    format: (v) => `${Math.round(v)}`,
    note: "",
  },
};

export function climateColor(
  metric: ClimateMetricId,
  scenario: string,
  horizon: string,
): unknown[] {
  const def = CLIMATE_METRICS[metric];
  const value = ["get", `${def.prefix}_${climateKey(scenario, horizon)}`];
  return [
    "case",
    ["==", value, null],
    "rgba(0,0,0,0)",
    ["interpolate", ["linear"], value, ...def.stops.flatMap((s) => [s.value, s.color])],
  ];
}

/** DRIAS' own horizon definitions, so no caller has to know what "H2" means. */
export const HORIZON_LABELS: Record<string, string> = {
  REF: "1976–2005",
  H1: "2021–2050",
  H2: "2041–2070",
  H3: "2071–2100",
};

export function climateLegend(
  metric: ClimateMetricId,
  scenario: string,
  horizon: string,
): Legend {
  const def = CLIMATE_METRICS[metric];
  const period = HORIZON_LABELS[horizon] ?? horizon;
  const when = horizon === "REF" ? `observed ${period}` : `${scenario}, ${period}`;
  return {
    unit: `${def.label}, ${def.unit} · ${when}`,
    stops: def.stops.map((s, i) => {
      const next = def.stops[i + 1];
      return {
        color: s.color,
        label: next ? `${def.format(s.value)} – ${def.format(next.value)}` : `${def.format(s.value)}+`,
      };
    }),
    note:
      def.note ||
      "Model cells are about 8 km across — read them for a commune, not a street.",
  };
}

export const CLIMATE_FILL = "climate-fill";

const DEFAULT_METRIC: ClimateMetricId = "tropical_nights";
const DEFAULT_SCENARIO = "RCP4.5";
const DEFAULT_HORIZON = "H2";

export const climateGridLayer: LayerDef = {
  id: "climate_grid",
  label: "Climate projection",
  group: "climate",
  description:
    "Projected heat, drought and heavy rain on the climate model's own ~8 km grid. Drawn as the grid it is, because that is the resolution it has.",
  attribution:
    '<a href="https://www.data.gouv.fr/datasets/indices-annuels-de-temperature-et-nombre-de-jours-de-temperature-issus-du-modele-aladin-climat">ALADIN-Climat · CNRM / Météo-France</a>',
  defaultVisible: false,
  legend: climateLegend(DEFAULT_METRIC, DEFAULT_SCENARIO, DEFAULT_HORIZON),
  legendFor: (ctx: LegendContext) =>
    climateLegend(ctx.climateMetric as ClimateMetricId, ctx.climateScenario, ctx.climateHorizon),
  sources: {
    climate_grid: { type: "vector", url: "pmtiles:///tiles/climate_grid.pmtiles" },
  },
  layers: [
    {
      id: CLIMATE_FILL,
      type: "fill",
      source: "climate_grid",
      "source-layer": "climate_grid",
      paint: {
        "fill-color": climateColor(DEFAULT_METRIC, DEFAULT_SCENARIO, DEFAULT_HORIZON) as never,
        "fill-opacity": 0.55,
      },
    },
    {
      id: "climate-grid-line",
      type: "line",
      source: "climate_grid",
      "source-layer": "climate_grid",
      // The mesh is drawn rather than smoothed away: seeing the cells is how a
      // reader knows not to take the colour under one building literally.
      paint: { "line-color": "#ffffff", "line-width": 0.5, "line-opacity": 0.5 },
    },
  ],
};

/**
 * Three hazard classes, cool to hot, plus a −1 the source uses for blocks it
 * did not evaluate — mostly fields and woodland, where there is no urban heat island to
 * speak of. Left transparent rather than coloured as "low", because "not
 * assessed" and "assessed as cool" are different statements.
 */
export const HEAT_CLASSES: { value: number; color: string; label: string }[] = [
  { value: 1, color: "#bcd9e8", label: "Low" },
  { value: 2, color: "#f6c445", label: "Moderate" },
  { value: 3, color: "#b3123c", label: "Strong" },
];

export type HeatWhen = "day" | "night";

export function heatColor(when: HeatWhen): unknown[] {
  return [
    "match",
    ["get", when === "day" ? "alea_j_cl" : "alea_n_cl"],
    ...HEAT_CLASSES.flatMap((c) => [c.value, c.color]),
    "rgba(0,0,0,0)",
  ];
}

export function heatLegend(when: HeatWhen): Legend {
  return {
    unit: `Heat island, ${when === "day" ? "daytime" : "night-time"}`,
    stops: HEAT_CLASSES.map((c) => ({ color: c.color, label: c.label })),
    note:
      when === "day"
        ? "Daytime heat is surfaces and shade. Unshaded blocks were not assessed — mostly fields and woodland."
        : "Night-time heat is the mass that spent the day storing it, and it is what stops you sleeping.",
  };
}

export const HEAT_FILL = "heat-fill";

export const heatIslandLayer: LayerDef = {
  id: "heat_island",
  label: "Urban heat island",
  group: "climate",
  description:
    "How much hotter each city block runs than its surroundings, by day and by night, from its built form and surfaces.",
  attribution:
    '<a href="https://data.iledefrance.fr/explore/dataset/ilots-de-chaleur-urbains-icu-classification-des-imu-en-zone-climatique-locale-lc/">Îlots de chaleur urbains · Institut Paris Region</a>',
  defaultVisible: false,
  legend: heatLegend("day"),
  legendFor: (ctx: LegendContext) => heatLegend(ctx.heatWhen === "night" ? "night" : "day"),
  // 237,000 blocks in a 77 MB archive, the same weight as air-noise — and the
  // same behaviour: hiding it throws every tile away, so switching it back on
  // re-parses the lot. Warmed instead, and cooled again as soon as the camera
  // moves.
  keepWarm: true,
  sources: {
    icu: { type: "vector", url: "pmtiles:///tiles/icu.pmtiles" },
  },
  layers: [
    {
      id: HEAT_FILL,
      type: "fill",
      source: "icu",
      "source-layer": "icu",
      minzoom: 10,
      paint: {
        "fill-color": heatColor("day") as never,
        "fill-opacity": 0.55,
      },
    },
  ],
};


/**
 * Forest fires actually recorded, per commune, since 2006.
 *
 * Shaded by **area burned**, not by number of fires, and that choice is the
 * whole design. Île-de-France records one fire in 2006 and sixty-seven in 2020,
 * which is a forest office that started logging every ignition rather than a
 * region that caught fire sixty times more often. Hectares survive that;
 * counts do not.
 *
 * Even so the scale is small — 283 ha across twenty years, against 65 ha in one
 * afternoon at Brunoy in 2006 — so the ramp is tuned to that range rather than
 * to a Mediterranean one, and the panel says what the numbers are worth.
 */
const FIRE_STOPS = [
  { value: 0, color: "rgba(0,0,0,0)" },
  { value: 0.5, color: "#fee8c8" },
  { value: 2, color: "#fdbb84" },
  { value: 8, color: "#e34a33" },
  { value: 25, color: "#7f0000" },
];

export function fireColor(): unknown[] {
  const value = ["get", "fire_ha"];
  return [
    "case",
    // Zero and null are the same picture here — no recorded fire — and both
    // should read as ground rather than as the bottom of a scale.
    ["any", ["==", value, null], ["<=", value, 0]],
    "rgba(0,0,0,0)",
    ["interpolate", ["linear"], value, ...FIRE_STOPS.flatMap((s) => [s.value, s.color])],
  ];
}

export const FIRE_FILL = "fires-fill";

export const firesLayer: LayerDef = {
  id: "fires",
  label: "Forest fires",
  group: "climate",
  description:
    "Hectares burned per commune since 2006, from the national fire database. Shaded by area rather than by number of fires, because the counts track reporting effort.",
  attribution:
    '<a href="https://bdiff.agriculture.gouv.fr/">BDIFF · Ministère de l\u2019Agriculture</a>',
  defaultVisible: false,
  legend: {
    unit: "hectares burned, 2006–2025",
    stops: FIRE_STOPS.slice(1).map((s, i) => {
      const next = FIRE_STOPS[i + 2];
      return { color: s.color, label: next ? `${s.value} – ${next.value} ha` : `${s.value} ha+` };
    }),
    note: "Unshaded communes have no recorded fire. Counts track reporting effort; area burned does not.",
  },
  sources: {
    fires: { type: "vector", url: "pmtiles:///tiles/fires_commune.pmtiles" },
  },
  layers: [
    {
      id: FIRE_FILL,
      type: "fill",
      source: "fires",
      "source-layer": "fires",
      paint: {
        "fill-color": fireColor() as never,
        // Fades as you zoom in. A commune total is a regional comparison; at
        // street zoom it says nothing about the block under the cursor and a
        // flat 0.7 wash just hides the map you came to read.
        "fill-opacity": [
          "interpolate", ["linear"], ["zoom"], 8, 0.7, 12, 0.45, 15, 0.18,
        ] as never,
      },
    },
  ],
};
